import { tzOffsetMinutes, zonedParts, EVENT_META, eventCategory, type BabyEvent, type EventCategory } from '@bb/shared/core';
import { platform } from './platform';

/* ---------- Тема ---------- */
export type ThemeMode = 'auto' | 'day' | 'night';

export function loadThemeMode(): ThemeMode {
  try {
    const v = localStorage.getItem('theme');
    if (v === 'day' || v === 'night' || v === 'auto') return v;
  } catch {
    /* нет хранилища */
  }
  return 'auto';
}

export function saveThemeMode(m: ThemeMode) {
  try {
    localStorage.setItem('theme', m);
  } catch {
    /* нет хранилища */
  }
}

/** «Авто»: ночь с 22:00 до 7:00 или если мессенджер в тёмной теме. */
export function resolveTheme(mode: ThemeMode, now = new Date()): 'day' | 'night' {
  if (mode !== 'auto') return mode;
  const h = now.getHours();
  return h >= 22 || h < 7 || platform.prefersDark() ? 'night' : 'day';
}

/* ---------- Время в часовом поясе семьи ---------- */

/** Дата и время «стенных часов» семьи → момент UTC в ISO. */
export function zonedToIso(date: string, time: string, tz: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const [hh, mm] = time.split(':').map(Number) as [number, number];
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  let ms = wall - tzOffsetMinutes(wall, tz) * 60000;
  // Второй проход — на случай перехода через смену смещения.
  ms = wall - tzOffsetMinutes(ms, tz) * 60000;
  return new Date(ms).toISOString();
}

/** Момент → { date: 'YYYY-MM-DD', time: 'HH:MM' } в поясе семьи. */
export function isoToZoned(iso: string | number, tz: string): { date: string; time: string } {
  const p = zonedParts(typeof iso === 'number' ? iso : Date.parse(iso), tz);
  const two = (n: number) => String(n).padStart(2, '0');
  return { date: `${p.year}-${two(p.month)}-${two(p.day)}`, time: `${two(p.hour)}:${two(p.minute)}` };
}

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];

export function humanDate(date: string, today: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const base = `${d} ${MONTHS[m - 1]}`;
  if (date === today) return `Сегодня, ${base}`;
  const t = Date.UTC(y, m - 1, d);
  const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
  const diff = Math.round((Date.UTC(ty, tm - 1, td) - t) / 86400000);
  if (diff === 1) return `Вчера, ${base}`;
  const wd = WEEKDAYS[new Date(t).getUTCDay()]!;
  return `${wd[0]!.toUpperCase()}${wd.slice(1)}, ${base}${y !== ty ? ` ${y}` : ''}`;
}

export function categoryOf(type: BabyEvent['type']): EventCategory {
  return EVENT_META[type].category;
}

/** Категория конкретной записи (у своих событий — своя). */
export function categoryOfEvent(e: Pick<BabyEvent, 'type' | 'payload'>): EventCategory {
  return eventCategory(e);
}

export function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?';
}

export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b === 1) return one;
  if (b >= 2 && b <= 4) return few;
  return many;
}
